import React, { useMemo, useRef, useEffect, useState } from "react";
import i18n, { t } from "i18next";
import FeatherIcon from "feather-icons-react";
import {
    formatLastName,
    formatSets,
    formatSetsWinner,
    getGroupRoundNumber,
    getOrdinalSuffix,
    getRoundTitle,
    groupCharCode,
    MatchStatus
} from "../../../constants";
import { renderParticipantRow } from "../../../components/renderParticipantRow";
import { useTheme } from "../../../contexts/ThemeContext";
import { chipsMatchEntity, searchStateClass } from "../../../utils/searchMatch";
import { publishTournamentSearchOptions } from "../../../utils/tournamentSearchOptionsBus";
import useAxios from "../../../utils/useAxios";


const ScheduleTable = ({
                           id,
                           courts,
                           setCourts,
                           rounds,
                           setRounds,
                           onOpenMatchModal,
                           onDeleteMatch,
                           onMoveMatch,
                           onSwapMatches,
                           tournament,
                           tournamentGrade,
                           onOpenMatchScore,
                           is_authorized,
                           canEdit,
                           onPickChange,
                           pickerTarget,
                           pickedCandidateMatch,
                           onPlaceCandidateMatch,
                           onCancelCandidateMatch,
                           onCancelPickerTarget,
                           searchChips = [],
                           scheduleView,
                           cancelPickSignal
                       }) => {

    // Self-managed (not received as a prop) - useAxios() returns a brand
    // new axios instance on every call, so receiving it as a prop from
    // TournamentSchedule.js (itself re-created on every one of ITS renders)
    // meant this component's own React.memo below always saw a "changed"
    // prop and re-rendered regardless of anything else being stable - same
    // pattern Bracket.js/BracketClassic.js/TournamentGroupTable.js already
    // use for the identical reason.
    const api = useAxios();
    const { compactTable } = useTheme();
    const [pickedMatch, setPickedMatch] = useState(null);

    // Tells the parent page when a pick-and-place move is in progress, so it
    // can hide the persistent matches bottom sheet (see TournamentSchedule.js)
    // for its duration - that sheet is a fixed, full-width strip pinned to
    // the very bottom of the viewport, and a destination cell the user needs
    // to tap to complete the move routinely lands underneath it (that's
    // exactly the strip's own tap-to-expand hit area), silently swallowing
    // the tap instead of reaching the table cell.
    useEffect(() => {
        onPickChange?.(!!pickedMatch);
    }, [pickedMatch, onPickChange]);

    // A tap meant to just open a match's score sheet can land inside
    // handleTouchStart's own 400ms long-press window below (easy to cross
    // by accident once you've had to scroll down and aim more carefully for
    // a match several rounds in) and still end up setting pickedMatch - the
    // matches sheet then stays non-interactive (see onPickChange above)
    // for a "move" the user never actually intended, so a tap on its own
    // close handle falls straight through to whatever table cell is
    // underneath instead of closing it. That cell's own onOpenMatchScore
    // reopens the sheet (jumping the page back to the top - openFull's own
    // scrollTo(0,0), useBottomSheet.js) instead of ever closing, which read
    // as "the sheet just won't close". expandSignal (bumped on every
    // legitimate "view this match" open, TournamentSchedule.js) is
    // therefore also the right signal to cancel any stray pick - viewing a
    // match's score and mid-move-ing one are mutually exclusive intents on
    // the same tap.
    useEffect(() => {
        if (cancelPickSignal !== undefined) setPickedMatch(null);
    }, [cancelPickSignal]);
    const [isTouchDevice, setIsTouchDevice] = useState(false);

    const longPressTimer = useRef(null);
    const timeInputRefs = useRef({});

    const openTimePicker = (roundId) => {
        const el = timeInputRefs.current[roundId];
        if (!el) return;
        if (el.showPicker) el.showPicker();
        else el.focus();
    };

    useEffect(() => {
        const onTouch = () => setIsTouchDevice(true);
        window.addEventListener('touchstart', onTouch, { once: true });
        return () => window.removeEventListener('touchstart', onTouch);
    }, []);

    // Stage/group/bracket/player chip options for the global search box
    // (Tournament.js) - it owns the search UI (one icon in the header now,
    // same as every other tab) but not this tab's data, see
    // tournamentSearchOptionsBus's own comment.
    const searchOptions = useMemo(() => {
        const players = new Map();
        const stages = new Set();
        const groups = new Set();
        const draws = new Set();
        rounds.forEach(r => {
            if (!r.match) return;
            const m = r.match;
            [1, 2, 3, 4].forEach(num => {
                const p = m[`player_${num}_name`];
                if (p && p.last_name) {
                    const fullName = `${p.last_name} ${p.first_name || ''}`.trim();
                    players.set(fullName, { value: `p_${fullName}`, label: fullName, type: 'player' });
                }
            });
            if (m.stage_name) stages.add(m.stage_name);
            if (m.group_number) groups.add(m.group_number);
            if (m.draw_number) draws.add(m.draw_number); // Собираем draw_number
        });
        const options = [
            { label: t("Stages"), options: Array.from(stages).map(s => ({ value: `s_${s}`, label: s, type: 'stage', realValue: s })) },
            { label: t("Groups"), options: Array.from(groups).map(g => ({ value: `g_${g}`, label: `${t("Group")} ${groupCharCode(g)}`, type: 'group', realValue: g })) },
            { label: t("Brackets"), options: Array.from(draws).sort((a,b) => a-b).map(d => ({ value: `d_${d}`, label: `${d}${getOrdinalSuffix(d)} ${t("Place")}`, type: 'draw', realValue: d })) },
            { label: t("Players"), options: Array.from(players.values()) }
        ];
        return options.filter(opt => opt.options.length > 0);
    }, [rounds]);

    useEffect(() => {
        publishTournamentSearchOptions(searchOptions);
    }, [searchOptions]);

    // true/false once a chip is active, null while there are none - stage/
    // group/draw chips test this match's own stage_name/group_number/
    // draw_number, everything else (player/free-text) tests the names of
    // whoever's actually playing (players 1-4 for singles/doubles, both
    // team names for team matches).
    const isMatchHighlighted = (match) => {
        if (!match) return searchChips.length > 0 ? false : null;
        const names = [1, 2, 3, 4].map(num => match[`player_${num}_name`])
            .concat([match.team_1_name, match.team_2_name])
            .filter(Boolean);
        return chipsMatchEntity(searchChips, {
            names,
            stageValue: match.stage_name,
            groupValue: match.group_number,
            drawValue: match.draw_number
        });
    };

    // Scrolls the first highlighted match into view - same technique as
    // TournamentPlayers.js/TournamentGroups.js's own version of this effect,
    // queried off the DOM (searchStateClass's own ".search-match") rather
    // than tracked through the render functions' own props, since there are
    // two of them (grid cell/agenda row) and this only needs to run once
    // per distinct match anyway.
    const lastScrolledMatchRef = useRef(null);
    useEffect(() => {
        if (searchChips.length === 0) {
            lastScrolledMatchRef.current = null;
            return;
        }
        const match = document.querySelector("#schedule-block .search-match");
        if (match && match !== lastScrolledMatchRef.current) {
            lastScrolledMatchRef.current = match;
            match.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [searchChips, rounds]);

    const handleTouchStart = (e, scheduleMatchId, sourceRoundId, sourceCourtId) => {
        if (!is_authorized) return;
        if (!isTouchDevice) setIsTouchDevice(true);
        if (pickedMatch) return;
        // Can't start moving an already-scheduled match while the add-picker
        // panel is docked open targeting a cell - see the same guard in
        // TournamentGroups.js.
        if (pickerTarget) return;
        // ...or while a candidate match is picked from the panel, waiting
        // for a destination tap - same reasoning, different picked-state.
        if (pickedCandidateMatch) return;

        clearTimeout(longPressTimer.current);
        const touch = e.touches[0];
        const startX = touch.clientX;
        const startY = touch.clientY;

        const timer = setTimeout(() => {
            if (navigator.vibrate) navigator.vibrate(50);
            setPickedMatch({ matchId: scheduleMatchId, srcRoundId: sourceRoundId, srcCourtId: sourceCourtId });
        }, 400);

        longPressTimer.current = timer;
        const target = e.currentTarget;

        const checkMove = (eMove) => {
            const touchMove = eMove.touches[0];
            if (Math.abs(touchMove.clientX - startX) > 10 || Math.abs(touchMove.clientY - startY) > 10) {
                clearTimeout(longPressTimer.current);
            }
        };

        const cleanup = () => {
            clearTimeout(longPressTimer.current);
            target.removeEventListener("touchmove", checkMove);
            target.removeEventListener("touchend", cleanup);
            target.removeEventListener("touchcancel", cleanup);
        };

        target.addEventListener("touchmove", checkMove, { passive: true });
        target.addEventListener("touchend", cleanup, { once: true });
        target.addEventListener("touchcancel", cleanup, { once: true });
    };

    const handleCellTap = (targetRoundId, targetCourtId, targetMatchId) => {
        if (!is_authorized) return;
        if (!pickedMatch) return;

        const src = pickedMatch;
        setPickedMatch(null);

        if (src.srcRoundId === targetRoundId && src.srcCourtId === targetCourtId) return;

        if (targetMatchId) {
            onSwapMatches(src.matchId, src.srcRoundId, src.srcCourtId, targetMatchId, targetRoundId, targetCourtId);
        } else {
            onMoveMatch(src.matchId, targetRoundId, targetCourtId);
        }
    };

    const handleDragStart = (e, scheduleMatchId, sourceRoundId, sourceCourtId) => {
        if (!is_authorized) return;
        e.dataTransfer.setData("matchId", scheduleMatchId);
        e.dataTransfer.setData("srcRoundId", sourceRoundId);
        e.dataTransfer.setData("srcCourtId", sourceCourtId);
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDrop = (e, targetRoundId, targetCourtId, targetMatchId) => {
        if (!is_authorized) return;
        e.preventDefault();

        // A drag started from the unscheduled-matches panel (Schedule
        // SelectMatchModal.js's own draggable rows), not an existing
        // scheduled match being repositioned - that drag already "picked"
        // its candidate via onPickCandidateMatch on dragstart, same as a
        // plain click on that row would with no target cell yet, so
        // finishing it here reuses the exact same place/cancel calls the
        // click-then-tap-a-cell flow already uses (see the empty-slot/
        // occupied-slot onClick handlers below for pickedCandidateMatch).
        if (e.dataTransfer.types.includes("schedulecandidatepick")) {
            if (targetMatchId) {
                onCancelCandidateMatch?.();
            } else if (pickedCandidateMatch) {
                onPlaceCandidateMatch(pickedCandidateMatch, targetRoundId, targetCourtId);
            }
            return;
        }

        const srcMatchId = e.dataTransfer.getData("matchId");
        const srcRoundId = e.dataTransfer.getData("srcRoundId");
        const srcCourtId = e.dataTransfer.getData("srcCourtId");
        if (!srcMatchId || (srcRoundId === targetRoundId && srcCourtId === targetCourtId)) {
            return;
        }

        if (targetMatchId) {
            onSwapMatches(srcMatchId, srcRoundId, srcCourtId, targetMatchId, targetRoundId, targetCourtId);
        } else {
            onMoveMatch(srcMatchId, targetRoundId, targetCourtId);
        }
    };

    const handleCourtChange = async (courtId, value) => {
        if (!is_authorized) return;
        setCourts(prev => prev.map(c => c.id === courtId ? { ...c, number: value } : c));
        await api.patch(`/api/v1.0/tournaments/${id}/schedules/courts/${courtId}/`, { name: String(value) });
    };

    const handleTimeChange = async (roundId, value) => {
        if (!is_authorized) return;
        setRounds(prev => prev.map(s => s.id === roundId ? { ...s, start_time: value } : s));
        await api.patch(`/api/v1.0/tournaments/${id}/schedules/schedule-rounds/${roundId}/`, { start: value });
    };

    const rows = useMemo(() => {
        const grouped = {};
        rounds.forEach(round => {
            if (!grouped[round.order]) grouped[round.order] = {};
            grouped[round.order][round.court] = round;
        });
        return grouped;
    }, [rounds]);
    const orderKeys = Object.keys(rows);

    // Round/time label - normally the row header (standard mode: rounds as
    // rows), reused as a column header in compact mode (courts as rows,
    // rounds as columns instead - see .schedule-table-transposed below).
    // Same content either way, just a <td> in one layout and a <th> in the
    // other.
    const renderRoundHeader = (order, index) => {
        const rowCells = Object.values(rows[order]);
        const firstRoundInRow = rowCells[0];
        return (
            <>
                <div className="round"><center>{index + 1}</center></div>
                {
                    is_authorized ?
                        <div className="schedule-time-wrap">
                            <input
                                ref={el => { if (el) timeInputRefs.current[firstRoundInRow.id] = el; }}
                                type="time"
                                className="schedule-time-input"
                                value={firstRoundInRow?.start_time || ""}
                                onChange={e => handleTimeChange(firstRoundInRow.id, e.target.value)}
                            />
                            {/* the native time input's own text is hidden (see
                                .schedule-time-input in index.css) - the browser
                                renders its HH:MM segments at a fixed intrinsic
                                width no CSS `width` can shrink below (~77px,
                                measured), far past this 38px button, so the real
                                digits were clipped off and invisible. This label
                                shows the same value at a size that actually fits,
                                sitting on top (pointer-events: none) so clicks
                                still reach the real input underneath. */}
                            {firstRoundInRow?.start_time && (
                                <span className="schedule-time-value">{firstRoundInRow.start_time}</span>
                            )}
                            <FeatherIcon
                                icon="clock"
                                className="schedule-time-icon"
                                onClick={() => openTimePicker(firstRoundInRow.id)}
                            />
                        </div> : <center className="schedule-round-time">{firstRoundInRow?.start_time}</center>
                }
            </>
        );
    };

    // Single match/empty-slot cell - identical in both layouts (standard:
    // rounds×courts, compact: courts×rounds), the round/court pair passed
    // in is all either one needs; only the surrounding table's row/column
    // order differs.
    const renderMatchCell = (round, court, key) => {
        const match = round?.match;
        const isThisPicked = pickedMatch?.matchId === round.schedule_match_id;
        const isThisPickerTarget = pickerTarget?.roundId === round.id && pickerTarget?.courtId === court.id;

        const highlighted = isMatchHighlighted(match);

        return (
            <td
                key={key}
                className="schedule-cell"
                data-drop-zone={is_authorized ? "true" : "false"}
                onDragOver={e => {
                    if (is_authorized) {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                    }
                }}
                onDrop={e => {
                    if (is_authorized) {
                        handleDrop(e, round.id, court.id, match ? round.schedule_match_id : null);
                    }
                }}
            >
                {match ? (
                    <div
                        // Distinct from the empty-slot div's own key below - both
                        // branches render a plain <div>, so without this React
                        // reconciles them as the *same* DOM node (same position,
                        // same tag) and just patches its class/style in place
                        // instead of unmounting/remounting. That in-place patch is
                        // a real CSS transition on an element the cursor is very
                        // likely still sitting over right after tapping this card's
                        // own delete "x" (see .schedule-match-delete) - the global
                        // `*:hover { transition: var(--speed) }` rule (index.css)
                        // then animates the swap from the match card's own styling
                        // to the empty-slot's, instead of the instant swap a fresh
                        // element gets (a brand-new node never transitions on its
                        // first paint, no matter which pseudo-classes it already
                        // matches). Forcing a real remount here is what keeps the
                        // "+ Match" button from visibly morphing out of the just-
                        // deleted card instead of just appearing.
                        key="match"
                        draggable={is_authorized && !isTouchDevice && !pickedMatch && !pickerTarget && !pickedCandidateMatch}
                        onDragStart={e => handleDragStart(e, round.schedule_match_id, round.id, court.id)}
                        onTouchStart={e => handleTouchStart(e, round.schedule_match_id, round.id, court.id)}
                        onContextMenu={e => e.preventDefault()}
                        className={`scheduled-match
                      ${[5, 6, 7].includes(match.status) ? "completed-block" : ""}
                      ${[2, 3, 4].includes(match.status) ? "in-progress-block" : ""}
                      ${isThisPicked ? "picked" : ""}
                      ${searchStateClass(highlighted)}
                    `}
                        style={{
                            cursor: !is_authorized ? "pointer" : (pickedMatch || pickedCandidateMatch) ? "crosshair" : "grab",
                            touchAction: "auto",
                            userSelect: "none",
                            WebkitUserSelect: "none",
                            WebkitTouchCallout: "none",
                            padding: "10px",
                            // the extra clearance below (16px, vs 10px on every other
                            // side) only exists so the title line doesn't collide with
                            // the admin-only delete "x" (absolutely positioned in the
                            // top-right corner) - was unconditional, so the public view
                            // (no delete button at all) got a visibly lopsided card for
                            // no reason.
                            paddingRight: is_authorized ? 0 : "10px",
                            borderRadius: "5px",
                            minHeight: "115px",
                            width: "100%",
                            boxSizing: "border-box",
                            overflow: "hidden",
                            position: "relative",
                            transition: "opacity 0.2s, border 0.2s"
                        }}
                    >
                        {is_authorized && (
                            <span
                                className="schedule-match-delete"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    // Was a bare no-op here - with the picker docked open
                                    // next to this table (wide viewports), clicking around
                                    // between candidates leaves pickedCandidateMatch set to
                                    // whichever was clicked last, and every delete click on
                                    // this table silently died with no feedback and no way
                                    // out except the small floating "cancel" pill. Cancelling
                                    // the pending pick here instead means the first click
                                    // always does *something* visible.
                                    if (pickedMatch) return;
                                    if (pickedCandidateMatch) {
                                        onCancelCandidateMatch?.();
                                        return;
                                    }
                                    onDeleteMatch(round.schedule_match_id, match);
                                }}
                            >
                                <FeatherIcon icon="x" size={14} />
                            </span>
                        )}
                        <div onClick={(e) => {
                            if (pickedMatch) {
                                e.stopPropagation();
                                handleCellTap(round.id, court.id, round.schedule_match_id);
                                return;
                            }
                            // A candidate match is picked from the panel - an
                            // occupied cell isn't a valid drop target. Cancels
                            // the pick instead of silently no-oping (see the
                            // delete "x" above, same reasoning): the panel can
                            // stay open across several picks, so clicking
                            // through a few candidates while just browsing
                            // left this stuck on and every click on a
                            // scheduled match underneath silently did nothing.
                            if (pickedCandidateMatch) {
                                onCancelCandidateMatch?.();
                                return;
                            }
                            if (onOpenMatchScore) {
                                onOpenMatchScore(match.id);
                            }
                        }} style={{ cursor: "pointer", paddingRight: is_authorized ? "16px" : 0, minWidth: 0 }}>
                            <div className="schedule-match-stage">
                                <span className="schedule-match-place">
                                    {match.group_number ? <>{t("Group")} {groupCharCode(match.group_number)}</> :
                                        <>{match.draw_number}{getOrdinalSuffix(match.draw_number)} {t("Place")}</>
                                    }
                                </span>
                                {(() => {
                                    const groupRound = getGroupRoundNumber(match.total, match.match_order);
                                    const roundText = match.group_number
                                        ? (groupRound ? `${t("Round")} ${groupRound}` : "")
                                        : getRoundTitle(match.round_number, match.total, t, i18n, match.match_order, false);
                                    if (!roundText && !match.match_number) return null;
                                    // Same single-line " · round #number" shape as the agenda
                                    // view's own .schedule-agenda-meta above - was a forced
                                    // second line (<br/>) here; .schedule-match-delete (the "x")
                                    // is absolutely positioned regardless, so collapsing this to
                                    // one line doesn't move it.
                                    return (
                                        <span className="schedule-match-round">
                                            {roundText && <> · {roundText}</>}
                                            {match.match_number && <> · #{match.match_number}</>}
                                        </span>
                                    );
                                })()}
                            </div>
                            <div style={{ lineHeight: "1.4" }}>
                            <span className={`schedule-player-row ${tournamentGrade === 2 ? "doubles" : ""} ${match.winner_side === 1 ? "match-winner" : match.winner_side ? "match-loser" : "match-expected"}`}>
                            <span>
                            {tournamentGrade === 3
                                ? renderParticipantRow(match.team_1_name || (match.team_1_number && `${t("Team")} ${match.team_1_number}`), null, match, 0, tournamentGrade, t, false)
                                : tournamentGrade === 2
                                    ? renderParticipantRow(match.player_1_name, match.player_3_name, match, 0, tournamentGrade, t)
                                    : renderParticipantRow(match.player_1_name, null, match, 0, tournamentGrade, t)
                            }
                            </span>
                            </span>
                                <div style={{ borderTop: "1px dashed var(--line)", padding: "3px 0", marginTop: "3px" }}></div>
                                <span className={`schedule-player-row ${tournamentGrade === 2 ? "doubles" : ""} ${match.winner_side === 2 ? "match-winner" : match.winner_side ? "match-loser" : "match-expected"}`}>
                                    {tournamentGrade === 3
                                        ? renderParticipantRow(match.team_2_name || (match.team_2_number && `${t("Team")} ${match.team_2_number}`), null, match, 1, tournamentGrade, t)
                                        : tournamentGrade === 2
                                            ? renderParticipantRow(match.player_2_name, match.player_4_name, match, 1, tournamentGrade, t)
                                            : renderParticipantRow(match.player_2_name, null, match, 1, tournamentGrade, t)
                                    }
                                </span>
                            </div>
                            <div className="schedule-match-footer">
                               <span>
                                    {Object.keys(match.sets || {}).length > 0
                                        ? (<span className="matches-points winner-points">{formatSetsWinner(match)}</span>)
                                        : <span className="matches-points score-placeholder">-:-</span>
                                    }
                                </span>
                                {(() => {
                                    const statusClass = [5, 6, 7].includes(match.status) ? "completed" : [2, 3, 4].includes(match.status) ? "in-progress" : "expected";
                                    const statusIcon = statusClass === "completed" ? "check" : statusClass === "in-progress" ? "activity" : "clock";
                                    return (
                                        <span className={`match-status-badge ${statusClass}`}>
                                            <FeatherIcon icon={statusIcon} />
                                            <span className="match-status-label">{t(MatchStatus[match.status])}</span>
                                        </span>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div
                        key="empty"
                        className="empty-slot"
                        style={{
                            cursor: !is_authorized ? "default" : (pickedMatch || pickedCandidateMatch) ? "crosshair" : "pointer"
                        }}
                        onClick={(e) => {
                            if (!is_authorized) return;
                            if (pickedMatch) {
                                e.stopPropagation();
                                handleCellTap(round.id, court.id, null);
                                return;
                            }
                            // A candidate match was picked from the still-open
                            // panel (no cell targeted yet) - any empty cell,
                            // including one that would otherwise open/retarget
                            // the panel below, just schedules it here instead.
                            if (pickedCandidateMatch) {
                                e.stopPropagation();
                                onPlaceCandidateMatch(pickedCandidateMatch, round.id, court.id);
                                return;
                            }
                            // This is the exact cell the picker panel is
                            // already open and targeting - a second click on
                            // it (the same "+ Match" button that opened it)
                            // closes it instead of just reopening with the
                            // same target again, which had no visible effect
                            // at all and read as "this button can't close
                            // anything".
                            if (isThisPickerTarget) {
                                onCancelPickerTarget?.();
                                return;
                            }
                            onOpenMatchModal(round.id, court.id, null);
                        }}
                    >
                        {(pickedMatch || pickedCandidateMatch)
                            ? <span className="player cursor group-add-participant">{t("Drop here")}</span>
                            : is_authorized && (
                                <span className={`player cursor group-add-participant ${isThisPickerTarget ? "picker-target" : ""}`}>
                                    + {t("Match")}
                                </span>
                            )
                        }
                    </div>
                )}
            </td>
        );
    };

    // Participant label for one side of a match - the agenda list (below)
    // puts both sides on one text line ("Сторона A – Сторона B"), which
    // renderParticipantRow can't do for singles (grade 1): it always
    // returns a block-level <div>, forcing its own line regardless of that
    // helper's own `br` param. Mirrors the same first-initial +
    // formatLastName formatting renderParticipantRow uses internally,
    // wrapping the initial in the same .player-name-initial span (see
    // index.css's compact/narrow-viewport rule hiding it) so a tight
    // one-line "Д. Іщенко / Л. Амбарнікова" has somewhere to give up width
    // before the actual surnames start getting squeezed.
    const matchSideLabel = (match, side) => {
        const fmtP = (p) => {
            if (!p) return null;
            if (typeof p === "string") return formatLastName(p);
            const last = p.last_name ? formatLastName(p.last_name) : "";
            if (!p.first_name) return last;
            return <><span className="player-name-initial">{p.first_name.charAt(0).toUpperCase()}. </span>{last}</>;
        };
        if (tournamentGrade === 3) {
            const team = side === 0
                ? (match.team_1_name || (match.team_1_number && `${t("Team")} ${match.team_1_number}`))
                : (match.team_2_name || (match.team_2_number && `${t("Team")} ${match.team_2_number}`));
            if (team) return typeof team === "string" ? team : (team.name || `${t("Team")} ${team.number || ""}`);
        }
        if (tournamentGrade === 2) {
            const p1 = fmtP(side === 0 ? match.player_1_name : match.player_2_name);
            const p2 = fmtP(side === 0 ? match.player_3_name : match.player_4_name);
            if (!p1 && !p2) return t("Participant");
            return <>{p1}{p1 && p2 ? " / " : ""}{p2}</>;
        }
        return fmtP(side === 0 ? match.player_1_name : match.player_2_name) || t("Participant");
    };

    // Compact mode's "Список" view (TournamentSchedule.js's scheduleView) -
    // one row per scheduled match, grouped by time, both sides on a single
    // line instead of the grid's two stacked ones ("давай твой вариант,
    // только в зебре и матч в одной строке"). Empty slots aren't listed
    // here at all - there's no natural "this is where nothing is" agenda
    // item, and creating/dragging a match still needs an actual grid (see
    // scheduleView's own "Сітка" option, the transposed table below).
    const renderAgendaList = () => {
        return (
            <div className="schedule-agenda">
                {orderKeys.map((order, index) => {
                    const rowCells = Object.values(rows[order]);
                    const firstRoundInRow = rowCells[0];
                    const items = courts
                        .map(court => ({ court, round: rows[order][court.id] }))
                        .filter(({ round }) => round?.match);
                    if (items.length === 0) return null;

                    // Resets at zero per time-slot group (not one counter shared
                    // across the whole list) - the divider above each group is
                    // where the eye resets its own expectation of the stripe
                    // rhythm, so a group inheriting odd carry-over parity from
                    // however many matches the *previous* group happened to have
                    // read as "one row broke the pattern" right after a divider,
                    // even though the full-list alternation was technically
                    // still consistent.
                    let rowIndex = 0;

                    return (
                        <React.Fragment key={order}>
                            <div className="schedule-agenda-divider">
                                <span className="t">{firstRoundInRow?.start_time || `${t("Round")} ${index + 1}`}</span>
                            </div>
                            {items.map(({ court, round }) => {
                                const match = round.match;
                                const zebra = rowIndex++ % 2 === 1;
                                const highlighted = isMatchHighlighted(match);
                                const statusClass = [5, 6, 7].includes(match.status) ? "completed" : [2, 3, 4].includes(match.status) ? "in-progress" : "expected";
                                const statusIcon = statusClass === "completed" ? "check" : statusClass === "in-progress" ? "activity" : "clock";
                                const groupRound = getGroupRoundNumber(match.total, match.match_order);
                                const roundText = match.group_number
                                    ? (groupRound ? `${t("Round")} ${groupRound}` : "")
                                    : getRoundTitle(match.round_number, match.total, t, i18n, match.match_order, false);

                                return (
                                    <div
                                        // NOT round.id - the backend reuses one round id across every
                                        // court at the same order/time-slot (confirmed via the API:
                                        // same id, different court/schedule_match_id), so it collides
                                        // across the courts.map() items in this same group. The grid
                                        // table never hit this - it keys cells by court.id instead
                                        // (unique within a row there). match.id is what's actually
                                        // unique per agenda row.
                                        key={match.id}
                                        className={`schedule-agenda-row ${zebra ? "zebra" : ""} ${searchStateClass(highlighted)}`}
                                        onClick={() => onOpenMatchScore && onOpenMatchScore(match.id)}
                                    >
                                        <span className="schedule-agenda-court">{court.number}</span>
                                        <div className="schedule-agenda-mm">
                                            <div className="schedule-agenda-meta">
                                                {match.group_number ? <>{t("Group")} {groupCharCode(match.group_number)}</> :
                                                    <>{match.draw_number}{getOrdinalSuffix(match.draw_number)} {t("Place")}</>}
                                                {roundText && <> · {roundText}</>}
                                                {match.match_number && <> #{match.match_number}</>}
                                            </div>
                                            <div className="schedule-agenda-sides">
                                                <span className={match.winner_side === 1 ? "win" : match.winner_side ? "lose" : ""}>{matchSideLabel(match, 0)}</span>
                                                <span className="schedule-agenda-sep">–</span>
                                                <span className={match.winner_side === 2 ? "win" : match.winner_side ? "lose" : ""}>{matchSideLabel(match, 1)}</span>
                                            </div>
                                        </div>
                                        <div className="schedule-agenda-right">
                                            <span className="schedule-agenda-score">
                                                {Object.keys(match.sets || {}).length > 0 ? formatSetsWinner(match) : "–:–"}
                                            </span>
                                            <span className={`match-status-badge ${statusClass}`}>
                                                <FeatherIcon icon={statusIcon} />
                                            </span>
                                            {is_authorized && (
                                                <span
                                                    className="schedule-agenda-delete"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onDeleteMatch(round.schedule_match_id, match);
                                                    }}
                                                >
                                                    <FeatherIcon icon="x" size={14} />
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    if (!courts.length && !rounds.length) return null;

    return (
        <>
            <div
                className={`schedule-table ${(canEdit && is_authorized) ? "" : "small_view"}`}
                style={{ position: "relative" }}
            >
                {pickedMatch && (
                    <div
                        onClick={() => setPickedMatch(null)}
                        style={{
                            position: "fixed",
                            // Same bottom/z-index as the equivalent picked-
                            // piece pill in Groups (TournamentGroups.js) and
                            // Grids (Bracket.js/BracketClassic.js) - z-index
                            // 100, well above .tournament-bottom-nav's own
                            // (3), is what actually keeps it from being
                            // painted over there, not the bottom offset.
                            bottom: "20px",
                            left: "50%",
                            transform: "translateX(-50%)",
                            backgroundColor: "#111827",
                            color: "white",
                            padding: "10px 20px",
                            borderRadius: "20px",
                            zIndex: 100,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "10px",
                            width: "fit-content",
                            cursor: "pointer",
                            userSelect: "none"
                        }}
                    >
                        <span style={{ fontSize: "14px", fontWeight: "500", whiteSpace: "nowrap" }}>{t("Tap destination cell")}</span>
                        <FeatherIcon icon="x" size={16} />
                    </div>
                )}

                {compactTable && scheduleView === "list" ? (
                    renderAgendaList()
                ) : compactTable ? (
                    // Transposed: courts as (sticky) rows, rounds/times as
                    // columns - "розклад компактніше" (мокап схвалено,
                    // компактний режим тільки). Same renderMatchCell/
                    // renderRoundHeader as the standard table below, just a
                    // different table.groups-table
                    <table className="schedule-table-transposed">
                        <thead>
                        <tr>
                            <th className="schedule-corner">{t("Court")}</th>
                            {orderKeys.map((order, index) => (
                                <th key={order}>
                                    {renderRoundHeader(order, index)}
                                </th>
                            ))}
                        </tr>
                        </thead>
                        <tbody>
                        {courts.map(court => (
                            <tr key={court.id}>
                                <td className="schedule-corner">
                                    {
                                        is_authorized ?
                                            <input
                                                type="number"
                                                value={court.number || ""}
                                                onChange={e => handleCourtChange(court.id, Number(e.target.value))}
                                            /> : <div>{court.number}</div>
                                    }
                                </td>
                                {orderKeys.map(order => renderMatchCell(rows[order][court.id], court, order))}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                ) : (
                    <table>
                        <thead>
                        <tr>
                            <th style={{
                                width: "50px",
                                color: is_authorized ? "" : "#fff",
                                background: is_authorized ? "var(--white)" : "var(--primary-green)"
                            }}>
                                {t("Round")}<br />{t("Time")}
                            </th>
                            {courts.map(court => (
                                <th key={court.id}
                                    style={{
                                        color: is_authorized ? "" : "#fff",
                                        background: is_authorized ? "var(--white)" : "var(--primary-green)"
                                }}>
                                    <center>
                                        {t("Court")}
                                        {
                                            is_authorized ?
                                                <input
                                                    type="number"
                                                    value={court.number || ""}
                                                    onChange={e => handleCourtChange(court.id, Number(e.target.value))}
                                                    style={{ width: "50px", textAlign: "center", marginBottom: "-5px" }}
                                                /> : <div style={{ margin: "1px" }}>{court.number}</div>
                                        }
                                    </center>
                                </th>
                            ))}
                        </tr>
                        </thead>
                        <tbody>
                        {orderKeys.map((order, index) => (
                            <tr key={order}>
                                <td>
                                    {renderRoundHeader(order, index)}
                                </td>
                                {courts.map(court => renderMatchCell(rows[order][court.id], court, court.id))}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                )}
            </div>

        </>
    );
};

// memo: this renders the whole schedule table/agenda in one shot, and its
// own parent (TournamentSchedule.js) re-renders on state that has nothing
// to do with the table itself (search chips, the score sheet or match
// picker opening, unscheduled-matches pool changes...) - without this,
// every one of those re-rendered the entire grid/agenda anyway. Only pays
// off because TournamentSchedule.js also stabilized (useCallback) every
// function prop passed here, and this component now manages its own `api`
// (see the comment on that above) instead of receiving TournamentSchedule's
// own per-render-fresh useAxios() instance as a prop - either gap alone
// would fail the shallow prop comparison below every time regardless of
// anything else being stable. Same reasoning as Bracket.js/BracketClassic.js/
// TournamentGroupTable.js's own identical pass.
export default React.memo(ScheduleTable);
